<script>
import { mapState } from 'vuex'
import ScheduleCustom from './ScheduleCustom.vue'
import CancelOrderDialog from '@/components/common/CancelOrderDialog.vue'
import { requiredIf, sameAs } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'

export default {
  components: { CancelOrderDialog, ScheduleCustom },
  mixins: [validationMixin],
  validations: {
    alertTestAndQa: {
      required: requiredIf(function () {
        return this.devStage }),
      sameAs: sameAs(() => true),
      checked(val) {
        return val }}
  },
  props: {
    value: { type: Object },
    order: { type: Object, default: null },
    loading: { type: Boolean },
    disabled: { type: Boolean },
    submitting: { type: Boolean },
    emptyList: { type: Boolean }
  },
  data() {
    return {
      selectedSchedule: { type: null, date: null },
      customDate: null,
      isScheduled: false,
      devStage: window.config.VUE_APP_STAGE !== 'PROD',
      alertTestAndQa: false,
      hasOrderAutocomplete: this.$permissions.hasOrderAutocomplete}
  },
  computed: {
    ...mapState({ userSettings: state => state.settings }),
    checkboxErrors() {
      const errors = []
      if (!this.$v.alertTestAndQa.$dirty) return errors
      if (!this.$v.alertTestAndQa.checked) {
        errors.push(this.$t('component.documentValidation.scheduleSelection.error.test.and.qa'))}
      return errors}
  },
  watch: {
    isScheduled(newValue) {
      if (newValue) {
        this.selectedSchedule.type = this.$enums.ScheduledType.Custom
        this.selectedSchedule.date = this.customDate
      } else {
        this.selectedSchedule.type = this.$enums.ScheduledType.Instantly
        this.selectedSchedule.date = null}
      this.$emit('input', this.selectedSchedule)
    }
  },
  created() {
    this.customDate = this.setSelectedScheduleDate()
    this.selectedSchedule.type = this.setSelectedScheduleType()
    this.isScheduled = this.selectedSchedule.type === this.$enums.ScheduledType.Custom
  },
  methods: {
    handleScheduleCustomInput(event) {
      this.selectedSchedule.date = event
    },
    setTime(date = null) {
      let hour = 14
      let minutes = 0
      if (date) {
        hour = this.momentDate(date).hour()
        minutes = this.momentDate(date).minutes() }
      return { hour: this.forceTwoDigits(hour), minutes: this.forceTwoDigits(minutes) }},
    setSelectedScheduleType() {
      return this.order.orderSchedule ? this.$enums.ScheduledType.Custom : this.$enums.ScheduledType.Instantly },
    setSelectedScheduleDate() {
      if (this.order.orderSchedule) {
        const autocompletionDate = this.order.orderSchedule.autocompletionDate
        return {
          date: this.momentDate(autocompletionDate).format('YYYY-MM-DD'),
          time: {
            hour: this.forceTwoDigits(this.momentDate(autocompletionDate).hour()),
            minutes: this.forceTwoDigits(this.momentDate(autocompletionDate).minutes()) } }}
      return { date: new Date().toISOString().substr(0, 10), time: this.setTime() }}
  }
}
</script>
<template>
  <v-card outlined elevation="0">
    <v-toolbar color="primary" dark height="48">
      <v-spacer />
      <v-toolbar-title class="text-center resetWhitespace">
        <h2 class="headline text--primary-2">{{ $t('component.documentValidation.scheduleSelection.title') }}</h2>
      </v-toolbar-title>
      <v-spacer />
    </v-toolbar>
    <v-card-text>
      <v-row>
        <v-col class="pb-0">
          <v-checkbox
            v-if="devStage"
            id="alertTestAndQa"
            v-model="alertTestAndQa"
            class="mt-0"
            :label="$t('component.documentValidation.scheduleSelection.alert.test.and.qa')"
            :error-messages="checkboxErrors"
            required
            @change="$v.alertTestAndQa.$touch()"
          ></v-checkbox>
        </v-col>
      </v-row>
      <v-checkbox
        v-if="hasOrderAutocomplete"
        id="shippingScheduleCheckbox"
        v-model="isScheduled"
        class="font-weight-bold"
        :disabled="disabled">
        <template #label>
          <div>{{$t('component.documentValidation.scheduleSelection.schedule.checkbox')}}</div>
          <v-tooltip top>
            <template #activator="{ on, attrs }">
              <v-icon
                color="primary"
                class="pl-1"
                dark
                v-bind="attrs"
                v-on="on">
                mdi-information-outline
              </v-icon>
            </template>
            <span v-html="$t('component.documentValidation.scheduleSelection.schedule.tooltip')" />
          </v-tooltip>
        </template>
      </v-checkbox>
      <v-row v-if="isScheduled">
        <v-col class="ml-9 pt-0 pb-0">
          <ScheduleCustom
            v-model="customDate"
            :disabled="disabled"
            @input="handleScheduleCustomInput($event)" />
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="justify-center">
      <v-tooltip top :disabled="!disabled">
        <template #activator="{ on }">
          <v-btn
            id="finishOrder"
            class="text-center pa-3 normal-texttransform"
            :class="[disabled ? 'secondary' : 'tertiary']"
            :disabled="disabled || ($v.$invalid && devStage)"
            :loading="submitting"
            v-on="on"
            @click="$emit('handleClick')">
            {{ $t('component.documentValidation.scheduleSelection.next.step.finish') }}
          </v-btn>
        </template>
        <span v-if="emptyList">{{ $t('component.documentValidation.scheduleSelection.tooltip.step.finished.empty.list') }}</span>
        <span v-else>{{ $t('component.documentValidation.scheduleSelection.tooltip.step.finished.disabled') }}</span>
      </v-tooltip>
    </v-card-actions>
    <v-card-actions class="justify-center">
      <CancelOrderDialog
        v-if="!loading && !submitting"
        :order-id="order.orderId"
        route-to="dashboard"
        class="pa-3" />
    </v-card-actions>
  </v-card>
</template>
<style lang="scss" scoped>
.normal-texttransform {
  text-transform: none !important;
  letter-spacing: 0.1px;
}
</style>
